import React, { useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { formatDate } from '../lib/utils';
import { MapPin, Calendar, Circle, Square, Diamond, XCircle, CheckCircle2, Clock, AlertCircle, Wrench } from 'lucide-react';

// Status configuration matching the map - handles SCMejora formats
const getStatusConfig = (estado) => {
  if (!estado) {
    return {
      color: 'bg-gray-500',
      textColor: 'text-white',
      borderColor: 'border-gray-500',
      icon: Circle,
      label: 'Sin Estado'
    };
  }
  
  const estadoLower = estado.toLowerCase();
  
  // Completed / Finalized
  if (estadoLower.includes('finalizada') || estadoLower.includes('completada') || estadoLower.includes('resuelta') || estadoLower.includes('cerrada')) {
    return {
      color: 'bg-[#008040]',
      textColor: 'text-white',
      borderColor: 'border-[#008040]',
      icon: CheckCircle2,
      label: 'Completada'
    };
  }
  
  // In Progress / En Ejecución
  if (estadoLower.includes('ejecucion') || estadoLower.includes('progreso')) {
    return {
      color: 'bg-orange-500',
      textColor: 'text-white',
      borderColor: 'border-orange-500',
      icon: Clock,
      label: 'En Progreso'
    };
  }
  
  // En Reparación (but not finalizada)
  if (estadoLower.includes('reparacion') && !estadoLower.includes('finalizada')) {
    return {
      color: 'bg-orange-500',
      textColor: 'text-white',
      borderColor: 'border-orange-500',
      icon: Wrench,
      label: 'En Reparación'
    };
  }
  
  // Pending
  if (estadoLower.includes('pendiente')) {
    return {
      color: 'bg-gray-400',
      textColor: 'text-white',
      borderColor: 'border-gray-400',
      icon: Clock,
      label: 'Pendiente'
    };
  }
  
  // Rejected
  if (estadoLower.includes('rechazada')) {
    return {
      color: 'bg-pink-600',
      textColor: 'text-white',
      borderColor: 'border-pink-600',
      icon: XCircle,
      label: 'Rechazada'
    };
  }
  
  // Blocked
  if (estadoLower.includes('bloqueada')) {
    return {
      color: 'bg-pink-600',
      textColor: 'text-white',
      borderColor: 'border-pink-600',
      icon: XCircle,
      label: 'Bloqueada'
    };
  }
  
  // Open
  if (estadoLower.includes('abierta')) {
    return {
      color: 'bg-red-500',
      textColor: 'text-white',
      borderColor: 'border-red-500',
      icon: AlertCircle,
      label: 'Abierta'
    };
  }
  
  // Default
  return {
    color: 'bg-gray-500',
    textColor: 'text-white',
    borderColor: 'border-gray-500',
    icon: Circle,
    label: estado
  };
};

const PRIORITY_CONFIG = {
  1: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Muy Baja' },
  2: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Baja' },
  3: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Media' },
  4: { color: 'bg-orange-100 text-orange-800 border-orange-300', label: 'Alta' },
  5: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Urgente' }
};

// Map string/object priorities to numbers
const getPriorityNumber = (prioridad) => {
  if (typeof prioridad === 'number') return prioridad;
  
  // Handle SCMejora object format: {codigo: "alta", nombre: "alta", color: "#..."}
  if (typeof prioridad === 'object' && prioridad !== null) {
    const code = (prioridad.codigo || prioridad.nombre || '').toLowerCase();
    const objMap = {
      'muy_alta': 5, 'urgente': 5, 'critica': 5,
      'alta': 4,
      'media': 3,
      'baja': 2,
      'muy_baja': 1
    };
    if (objMap[code]) return objMap[code];
    // Try to parse codigo as number (in case it's "3", "4", etc.)
    const num = parseInt(prioridad.codigo);
    return !isNaN(num) ? num : 3;
  }
  
  if (typeof prioridad === 'string') {
    const num = parseInt(prioridad);
    if (!isNaN(num)) return num;
    
    const priorityMap = {
      'muy_baja': 1,
      'baja': 2,
      'media': 3,
      'alta': 4,
      'muy_alta': 5,
      'urgente': 5,
      'critica': 5
    };
    return priorityMap[prioridad.toLowerCase()] || 3;
  }
  return 3;
};

export default function TasksList({ tasks, loading, selectedTaskId, onSelectTask, selectedTaskIds = [], onToggleTaskSelection }) {
  const listRef = useRef(null);
  const selectedCardRef = useRef(null);

  // Scroll to selected task when it changes
  useEffect(() => {
    if (selectedTaskId && selectedCardRef.current) {
      setTimeout(() => {
        if (selectedCardRef.current) {
          selectedCardRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      }, 100);
    }
  }, [selectedTaskId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No se encontraron tareas</p>
        <p className="text-sm text-muted-foreground mt-1">Intenta ajustar los filtros</p>
      </div>
    );
  }

  const handleCardClick = (task, e) => {
    if (e.target.type === 'checkbox') return;
    onSelectTask(task);
  };

  const getPriorityConfig = (prioridad) => {
    const num = getPriorityNumber(prioridad);
    return PRIORITY_CONFIG[num] || PRIORITY_CONFIG[3];
  };

  return (
    <div ref={listRef} className="space-y-2 p-4">
      {/* Results count */}
      <div className="text-xs text-muted-foreground mb-2 px-1">
        {tasks.length} incidencia{tasks.length !== 1 ? 's' : ''} encontrada{tasks.length !== 1 ? 's' : ''}
      </div>
      
      {tasks.map((task) => {
        const isSelected = selectedTaskIds.includes(task.id);
        const isActive = selectedTaskId === task.id;
        // Use estado_reparacion or estadoincidencia_id from backend response
        const statusConfig = getStatusConfig(task.estado_reparacion || task.estadoincidencia_id || task.estado);
        const priorityConfig = getPriorityConfig(task.prioridad_manual != null ? task.prioridad_manual : task.prioridad);
        const priorityNum = getPriorityNumber(task.prioridad_manual != null ? task.prioridad_manual : task.prioridad);
        const StatusIcon = statusConfig.icon;
        
        return (
          <Card
            key={task.id}
            ref={isActive ? selectedCardRef : null}
            className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden ${
              isActive 
                ? 'ring-2 ring-[#FFD700] shadow-lg bg-[#008040]/5' 
                : 'hover:bg-muted/50'
            } ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
            onClick={(e) => handleCardClick(task, e)}
            data-testid={`task-item-${task.id}`}
          >
            {/* Status color bar on left */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusConfig.color}`}></div>
            
            <div className="flex items-start space-x-3 pl-2">
              {/* Checkbox for bulk selection */}
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleTaskSelection(task.id)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                data-testid={`task-checkbox-${task.id}`}
              />
              
              {/* Status Icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full ${statusConfig.color} flex items-center justify-center`}>
                <StatusIcon className={`w-4 h-4 ${statusConfig.textColor}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                {/* Header row */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    {/* Code and selected badge */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {task.codigo}
                      </span>
                      {isActive && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#008040] text-white">
                          Seleccionada
                        </span>
                      )}
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-medium text-sm line-clamp-2 leading-tight">
                      {task.titulo || task.descripcion || 'Sin título'}
                    </h3>
                  </div>
                  
                  {/* Priority badge */}
                  <div className={`flex-shrink-0 px-2 py-1 rounded text-[10px] font-semibold border ${priorityConfig.color}`}>
                    {priorityConfig.label}{task.prioridad_manual != null ? ' ✎' : ''}
                  </div>
                </div>
                
                {/* Status badge */}
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${statusConfig.color} ${statusConfig.textColor} text-[10px] px-2 py-0`}>
                    {statusConfig.label}
                  </Badge>
                </div>
                
                {/* Meta info */}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                  {task.direccion && (
                    <div className="flex items-center min-w-0">
                      <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{task.direccion}</span>
                    </div>
                  )}
                  {task.fecha && (
                    <div className="flex items-center flex-shrink-0">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>{formatDate(task.fecha)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
